/**
 * The Teaching Philosophy Statement Generator: a first-person statement assembled from
 * the instructor's own beliefs, methods, evidence, assessment practice and plans.
 *
 * The statement is five paragraphs, one per section. Each is the instructor's words
 * placed inside a short first-person frame ("I believe that …", "I assess learning by
 * …"), with an optional example in full sentences. The tool writes no claims: a section
 * left blank stays a bracketed placeholder. Guides to teaching statements ask for the
 * first person in the present tense, concrete examples, and evidence that methods work,
 * in one to two single-spaced pages — about 500 words a page, which is the unit the
 * length target uses.
 *
 * Frame syntax is documented in `correspondence/compose.ts`.
 */

import { countWords } from "@/lib/tool-kit/admissions/counts";
import {
  asFragment,
  asSentence,
  cleanBlock,
  compose,
  orPlaceholder,
  segmentsOf,
} from "@/lib/tool-kit/correspondence/compose";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import { lowerFirstWord } from "@/lib/tool-kit/essay-templates/text";

export const TEACHING_SECTIONS = [
  "belief",
  "methods",
  "evidence",
  "assessment",
  "growth",
] as const;
export type TeachingSection = (typeof TEACHING_SECTIONS)[number];

/** A planning split in percent: methods carry most weight, then belief and evidence. */
export const TEACHING_SHARES: Record<TeachingSection, number> = {
  belief: 20,
  methods: 30,
  evidence: 20,
  assessment: 15,
  growth: 15,
};

export const WORDS_PER_PAGE = 500;
export const LENGTH_CHOICES = ["page1", "page15", "page2", "custom"] as const;
export type LengthChoice = (typeof LENGTH_CHOICES)[number];
export const CHOICE_WORDS: Record<Exclude<LengthChoice, "custom">, number> = {
  page1: 500,
  page15: 750,
  page2: 1000,
};
export const MIN_TARGET = 200;
export const MAX_TARGET = 2000;
/** Within this share of the target counts as on target. */
export const TOLERANCE = 0.1;

export interface TeachingInput {
  courses: string;
  belief: string;
  beliefExample: string;
  methods: string;
  methodsExample: string;
  evidence: string;
  assessment: string;
  feedback: string;
  growth: string;
  goal: string;
}

export const TEACHING_BANK = {
  placeholders: {
    belief: "your central belief about how students learn",
    methods: "what you do in class to act on that belief",
    evidence: "Evidence that it works: student work, outcomes, feedback",
    assessment: "how you assess learning",
    growth: "how you keep improving as a teacher",
  },
  belief: [
    "?courses:I teach {courses}.",
    "I believe that {belief}.",
    "?beliefExample:{beliefExample}",
  ],
  methods: ["In practice, I {methods}.", "?methodsExample:{methodsExample}"],
  evidence: ["{evidence}"],
  assessment: ["I assess learning by {assessment}.", "?feedback:{feedback}"],
  growth: [
    "I continue to develop as a teacher by {growth}.",
    "?goal:Next, I want to {goal}.",
  ],
} as const;

export interface TeachingBank {
  placeholders: Readonly<Record<TeachingSection, string>>;
  belief: readonly string[];
  methods: readonly string[];
  evidence: readonly string[];
  assessment: readonly string[];
  growth: readonly string[];
}

/** Words the writer wrote: placeholders in brackets do not count. */
export function writtenWords(paragraph: string): number {
  return segmentsOf(paragraph)
    .filter((segment) => !segment.placeholder)
    .reduce((total, segment) => total + countWords(segment.text), 0);
}

export interface TeachingParagraph {
  section: TeachingSection;
  text: string;
  words: number;
}

function fragment(raw: string): string {
  return lowerFirstWord(asFragment(raw), "en");
}

/** The statement, one paragraph per section, each with the words the writer gave it. */
export function buildTeachingStatement(
  input: TeachingInput,
  bank: TeachingBank
): TeachingParagraph[] {
  const set = (flags: Set<string>, name: string, on: boolean) => {
    if (on) {
      flags.add(name);
    }
  };
  const values: Record<string, string> = {
    courses: asFragment(input.courses),
    belief: orPlaceholder(fragment(input.belief), bank.placeholders.belief),
    beliefExample: asSentence(cleanBlock(input.beliefExample)),
    methods: orPlaceholder(fragment(input.methods), bank.placeholders.methods),
    methodsExample: asSentence(cleanBlock(input.methodsExample)),
    evidence: orPlaceholder(
      asSentence(cleanBlock(input.evidence)),
      bank.placeholders.evidence
    ),
    assessment: orPlaceholder(
      fragment(input.assessment),
      bank.placeholders.assessment
    ),
    feedback: asSentence(cleanBlock(input.feedback)),
    growth: orPlaceholder(fragment(input.growth), bank.placeholders.growth),
    goal: fragment(input.goal),
  };
  const flags = new Set<string>();
  for (const key of [
    "courses",
    "beliefExample",
    "methodsExample",
    "feedback",
    "goal",
  ]) {
    set(flags, key, values[key] !== "");
  }
  return TEACHING_SECTIONS.map((section) => {
    const text = compose(bank[section], values, flags).join(" ");
    return { section, text, words: writtenWords(text) };
  });
}

/** A length target in words: a chosen number of pages, or a typed count in range. */
export function targetWords(
  choice: LengthChoice,
  custom: number | null
): number | null {
  if (choice !== "custom") {
    return CHOICE_WORDS[choice];
  }
  return custom !== null && custom >= MIN_TARGET && custom <= MAX_TARGET
    ? custom
    : null;
}

/** Word targets per section, in tens, adding up to the total. */
export function sectionTargets(total: number): Record<TeachingSection, number> {
  const words = allocateWords(
    total,
    TEACHING_SECTIONS.map((section) => TEACHING_SHARES[section]),
    10
  );
  const targets = {} as Record<TeachingSection, number>;
  TEACHING_SECTIONS.forEach((section, index) => {
    targets[section] = words[index];
  });
  return targets;
}

export type LengthStatus = "empty" | "short" | "on" | "long";

/** Where a word count stands against a target, with a 10% tolerance either way. */
export function lengthStatus(words: number, target: number): LengthStatus {
  if (words === 0) {
    return "empty";
  }
  if (words < target * (1 - TOLERANCE)) {
    return "short";
  }
  return words > target * (1 + TOLERANCE) ? "long" : "on";
}
